import React from 'react';

/**
 * Whitelist-based HTML Sanitizer & Parser for React.
 *
 * Converts safe HTML markup into React element trees without using
 * dangerouslySetInnerHTML or relying on browser DOM APIs (DOMParser/document).
 * Works identically in Browser and Node.js / SSR / Vitest.
 *
 * Security:
 * - Strict whitelist of tags (formatting, structure, lists, tables, links).
 * - Disallowed tags (script, iframe, style, object, embed, etc.) have their tags and inner contents stripped.
 * - Event handlers (onclick, onerror, onload, etc.) and inline CSS style attributes are removed.
 * - Links (<a>) are restricted to safe protocols (http, https, mailto, tel, relative).
 * - target="_blank" links automatically receive rel="noopener noreferrer".
 * - HTML entities are decoded to standard Unicode characters.
 */

const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'small',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'div', 'span',
  'blockquote', 'code', 'pre', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'wbr'
]);

const VOID_TAGS = new Set(['br', 'hr', 'wbr']);
const STRIP_CONTENTS_TAGS = new Set([
  'script', 'style', 'iframe', 'frame', 'object', 'embed', 'applet',
  'form', 'textarea', 'select', 'button', 'svg', 'canvas', 'template', 'meta', 'link'
]);

const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'',
  nbsp: '\u00A0', iexcl: '¡', cent: '¢', pound: '£', curren: '¤',
  yen: '¥', brvbar: '¦', sect: '§', uml: '¨', copy: '©',
  ordf: 'ª', laquo: '«', not: '¬', shy: '\u00AD', reg: '®',
  macr: '¯', deg: '°', plusmn: '±', sup2: '²', sup3: '³',
  acute: '´', micro: 'µ', para: '¶', middot: '·', cedil: '¸',
  sup1: '¹', ordm: 'º', raquo: '»', frac14: '¼', frac12: '½',
  frac34: '¾', iquest: '¿',
  Agrave: 'À', Aacute: 'Á', Acirc: 'Â', Atilde: 'Ã', Auml: 'Ä', Aring: 'Å', AElig: 'Æ',
  Ccedil: 'Ç', Egrave: 'È', Eacute: 'É', Ecirc: 'Ê', Euml: 'Ë',
  Igrave: 'Ì', Iacute: 'Í', Icirc: 'Î', Iuml: 'Ï',
  ETH: 'Ð', Ntilde: 'Ñ', Ograve: 'Ò', Oacute: 'Ó', Ocirc: 'Ô', Otilde: 'Õ', Ouml: 'Ö',
  times: '×', Oslash: 'Ø', Ugrave: 'Ù', Uacute: 'Ú', Ucirc: 'Û', Uuml: 'Ü',
  Yacute: 'Ý', THORN: 'Þ', szlig: 'ß',
  agrave: 'à', aacute: 'á', acirc: 'â', atilde: 'ã', auml: 'ä', aring: 'å', aelig: 'æ',
  ccedil: 'ç', egrave: 'è', eacute: 'é', ecirc: 'ê', euml: 'ë',
  igrave: 'ì', iacute: 'í', icirc: 'î', iuml: 'ï',
  eth: 'ð', ntilde: 'ñ', ograve: 'ò', oacute: 'ó', ocirc: 'ô', otilde: 'õ', ouml: 'ö',
  divide: '÷', oslash: 'ø', ugrave: 'ù', uacute: 'ú', ucirc: 'û', uuml: 'ü',
  yacute: 'ý', thorn: 'þ', yuml: 'ÿ',
  euro: '€', ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’',
  sbquo: '‚', ldquo: '“', rdquo: '”', bdquo: '„', dagger: '†',
  Dagger: '‡', bull: '•', hellip: '…', permil: '‰', prime: '′',
  Prime: '″', lsaquo: '‹', rsaquo: '›', trade: '™'
};

/**
 * Decodes standard HTML entities into UTF-8 text.
 */
export function decodeHtmlEntities(str) {
  if (!str || typeof str !== 'string') return '';
  return str.replace(/&([a-zA-Z0-9]+|#\d+|#[xX][0-9a-fA-F]+);/g, (match, entity) => {
    if (NAMED_ENTITIES[entity]) return NAMED_ENTITIES[entity];
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      const code = parseInt(entity.slice(2), 16);
      return !isNaN(code) && code > 0 && code < 65536 ? String.fromCharCode(code) : match;
    }
    if (entity.startsWith('#')) {
      const code = parseInt(entity.slice(1), 10);
      return !isNaN(code) && code > 0 && code < 65536 ? String.fromCharCode(code) : match;
    }
    return match;
  });
}

/**
 * Validates and sanitizes a URL for safe rendering in href attributes.
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const clean = url.trim().replace(/[\x00-\x1F\x7F\s]+/g, '');
  if (/^(javascript|data|vbscript|file):/i.test(clean)) return null;
  if (/^(https?:|mailto:|tel:|\/|#|\.|\?)/i.test(clean) || (!clean.includes(':') && !clean.startsWith('//'))) {
    return clean;
  }
  return null;
}

/**
 * Strips all HTML markup returning only text.
 */
export function stripHtmlTags(html) {
  if (!html || typeof html !== 'string') return '';
  const withoutScripts = html.replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '');
  const stripped = withoutScripts.replace(/<[^>]+>/g, ' ');
  return decodeHtmlEntities(stripped).replace(/\s+/g, ' ').trim();
}

/**
 * Extracts and filters tag attributes against security whitelist.
 */
function parseSafeAttributes(attrString, tagName) {
  const props = {};
  if (!attrString) return props;
  const attrRegex = /([a-zA-Z0-9_\-:]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
  let match;
  while ((match = attrRegex.exec(attrString)) !== null) {
    const rawName = match[1].toLowerCase();
    const rawVal = match[2] !== undefined ? match[2] : match[3] !== undefined ? match[3] : match[4] !== undefined ? match[4] : '';
    const decodedVal = decodeHtmlEntities(rawVal);

    if (rawName.startsWith('on')) continue;
    if (rawName === 'style') continue;

    if (tagName === 'a') {
      if (rawName === 'href') {
        const safeHref = sanitizeUrl(decodedVal);
        if (safeHref) props.href = safeHref;
      } else if (rawName === 'target') {
        if (decodedVal === '_blank') {
          props.target = '_blank';
          props.rel = 'noopener noreferrer';
        } else if (['_self', '_top', '_parent'].includes(decodedVal)) {
          props.target = decodedVal;
        }
      } else if (rawName === 'rel' && !props.rel) {
        props.rel = decodedVal;
      } else if (rawName === 'title') {
        props.title = decodedVal;
      }
    } else if (rawName === 'title') {
      props.title = decodedVal;
    } else if (rawName === 'class' || rawName === 'classname') {
      props.className = decodedVal;
    } else if (rawName === 'id') {
      props.id = decodedVal;
    }
  }
  return props;
}

/**
 * Parses safe HTML into React element tree without dangerouslySetInnerHTML.
 * Falls back cleanly to text with line breaks when no HTML tags are present.
 */
export function parseHtmlToReact(html, keyPrefix = 'sn') {
  if (!html || typeof html !== 'string') return null;

  // Plain text optimization (preserve newlines with <br />)
  if (!html.includes('<')) {
    const lines = html.split('\n');
    if (lines.length === 1) return decodeHtmlEntities(html);
    const parts = [];
    lines.forEach((line, idx) => {
      if (idx > 0) parts.push(React.createElement('br', { key: `${keyPrefix}-br-${idx}` }));
      if (line) parts.push(decodeHtmlEntities(line));
    });
    return parts.length === 1 ? parts[0] : React.createElement(React.Fragment, null, ...parts);
  }

  const tagRegex = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z0-9\-]+)([^>]*)>/g;
  const root = { tag: 'root', props: {}, children: [] };
  const stack = [root];
  let lastIndex = 0;
  let match;
  let stripDepth = 0;

  while ((match = tagRegex.exec(html)) !== null) {
    const [fullMatch, isClosing, rawTagName, rawAttrs] = match;
    const textBefore = html.substring(lastIndex, match.index);
    lastIndex = tagRegex.lastIndex;

    const currentParent = stack[stack.length - 1];

    if (textBefore && stripDepth === 0) {
      const decoded = decodeHtmlEntities(textBefore);
      if (decoded) {
        if (currentParent.children.length > 0 && typeof currentParent.children[currentParent.children.length - 1] === 'string') {
          currentParent.children[currentParent.children.length - 1] += decoded;
        } else {
          currentParent.children.push(decoded);
        }
      }
    }

    if (fullMatch.startsWith('<!--')) {
      continue;
    }

    const tagName = (rawTagName || '').toLowerCase();

    if (STRIP_CONTENTS_TAGS.has(tagName)) {
      if (isClosing) {
        if (stripDepth > 0) stripDepth--;
      } else {
        if (!fullMatch.endsWith('/>')) {
          stripDepth++;
        }
      }
      continue;
    }

    if (stripDepth > 0) {
      continue;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      continue;
    }

    if (VOID_TAGS.has(tagName)) {
      currentParent.children.push({
        tag: tagName,
        props: parseSafeAttributes(rawAttrs, tagName),
        children: []
      });
      continue;
    }

    if (isClosing) {
      let foundIdx = -1;
      for (let i = stack.length - 1; i > 0; i--) {
        if (stack[i].tag === tagName) {
          foundIdx = i;
          break;
        }
      }
      if (foundIdx !== -1) {
        while (stack.length > foundIdx) {
          stack.pop();
        }
      }
    } else {
      const isSelfClosing = fullMatch.endsWith('/>');
      const node = {
        tag: tagName,
        props: parseSafeAttributes(rawAttrs, tagName),
        children: []
      };
      currentParent.children.push(node);
      if (!isSelfClosing) {
        stack.push(node);
      }
    }
  }

  const trailingText = html.substring(lastIndex);
  if (trailingText && stripDepth === 0) {
    const decoded = decodeHtmlEntities(trailingText);
    if (decoded) {
      const topParent = stack[stack.length - 1];
      if (topParent.children.length > 0 && typeof topParent.children[topParent.children.length - 1] === 'string') {
        topParent.children[topParent.children.length - 1] += decoded;
      } else {
        topParent.children.push(decoded);
      }
    }
  }

  function renderNode(node, idx) {
    if (typeof node === 'string') return node;
    const childElements = node.children.map((c, i) => renderNode(c, i));
    const props = { ...node.props, key: `${keyPrefix}-${node.tag}-${idx}` };
    return React.createElement(node.tag, props, ...childElements);
  }

  const rendered = root.children.map((c, i) => renderNode(c, i));
  return rendered.length === 1 ? rendered[0] : (rendered.length === 0 ? null : React.createElement(React.Fragment, null, ...rendered));
}

/**
 * React Component for safe sanitized HTML rendering.
 */
export function SanitizedHtml({ content, className = '', tag: Tag = null, keyPrefix = 'sn', ...props }) {
  if (!content) return null;
  const parsed = parseHtmlToReact(content, keyPrefix);
  if (!Tag) {
    return parsed;
  }
  return <Tag className={className} {...props}>{parsed}</Tag>;
}
